import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ethers } from 'ethers';
import {
    Box, Grid, Typography, Paper, Skeleton, Button, Stack, Container, Divider, Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp'; // Used for Claim button
import HistoryIcon from '@mui/icons-material/History';
import { useWeb3React } from '@web3-react/core';
import { injected } from '../../connector';
import { tokenContractAddress, abi as tokenAbi } from './components/StakeCard/services/consts';
import { setBalance as setWalletBalance } from '../../features/userWalletSlice';
// Components
import StakeCard from './components/StakeCard/StakeCard';
import WithdrawCard from './components/WithdrawCard/WithdrawCard';

// Services & Store
import { abi, stakingContractAddress } from './services/consts';
import { RpcProvider } from '../../consts/rpc';
import { setBalance as setStakeBalance } from '../../features/stakingSlice';
import { fetchUSDTPrice, fetchEbPrice } from '../../services/prices';
import { SALE_ADDRESS, SALE_ABI, TOKEN_ABI } from '../../consts/abi';

// --- CONSTANTS ---
const FIXED_DECIMALS = 18;
const ESIR_ADDRESS = '0x8C75a1C86C21b74754FC8e3Bc4e7f79B4fCC5a28';

// Optimized Compact Stat Component
const CockpitStat = ({ label, value, highlight = false, icon, compact = false }) => (
    <Box sx={{
        p: compact ? 2 : 3,
        border: '1px solid',
        borderColor: highlight ? 'primary.main' : 'rgba(255,255,255,0.1)',
        bgcolor: highlight ? 'rgba(210, 157, 92, 0.05)' : 'rgba(0,0,0,0.2)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderRadius: 2
    }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            {icon && <Box sx={{ color: highlight ? 'primary.main' : 'text.secondary', display: 'flex' }}>{icon}</Box>}
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, fontSize: '0.65rem' }}>
                {label}
            </Typography>
        </Stack>
        <Typography variant={compact ? "h5" : "h4"} fontWeight={700} sx={{ color: highlight ? 'primary.main' : 'text.primary' }}>
            {value}
        </Typography>
    </Box>
);

/* ─── StatBox (portfolio holdings card) ──────────────────────────── */
const StatBox = ({ label, value, subValue, breakdown, icon }) => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Paper sx={{
            p: 3,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: isDark ? 'rgba(10,16,25,0.7)' : 'background.paper',
            border: isDark
                ? '1px solid rgba(255,255,255,0.08)'
                : `1px solid ${theme.palette.divider}`,
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0,
                width: '4px', height: '100%',
                bgcolor: 'primary.main',
            },
        }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing="0.1em">
                        {label}
                    </Typography>
                    {icon}
                </Stack>

                <Box>
                    <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                        {value}
                    </Typography>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, opacity: 0.9 }}>
                        {subValue}
                    </Typography>
                </Box>

                <Divider />

                <Grid container spacing={1}>
                    {breakdown}
                </Grid>
            </Stack>
        </Paper>
    );
};

const AllocationStaking = () => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { activate, account, library } = useWeb3React();
    const dispatch = useDispatch();

    const [stats, setStats] = useState({
        tvl: 0,
        apy: 0,
        totalDistributed: 0,
        esirPrice: 0,
        ebondPrice: 0,
        myPending: 0
    });
    const [balances, setBalances] = useState({ ebondsWallet: 0, ebondsStaked: 0, esirWallet: 0, esirUnclaimed: 0 });
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false); // State for button loading

const fetchData = useCallback(async () => {
    try {
        setLoading(true);
        const provider = new ethers.providers.JsonRpcProvider(RpcProvider);

        // Contract Instances
        const contract = new ethers.Contract(stakingContractAddress, abi, provider);
        // Token contracts to read wallet balances
        const tokenContract = new ethers.Contract(tokenContractAddress, tokenAbi, provider);
        const esirContract  = new ethers.Contract(ESIR_ADDRESS, TOKEN_ABI, provider);
        const saleContract  = new ethers.Contract(SALE_ADDRESS, SALE_ABI, provider);

        const [esirP, ebondP] = await Promise.all([
            fetchUSDTPrice().catch(() => 0),
            fetchEbPrice().catch(() => 0)
        ]);

        const totalDepositsRaw = await contract.totalDeposits();
        const paidOutRaw = await contract.paidOut();

        const totalDeposits = Math.floor(parseFloat(ethers.utils.formatUnits(totalDepositsRaw, FIXED_DECIMALS)));
        const paidOut = Math.floor(parseFloat(ethers.utils.formatUnits(paidOutRaw, FIXED_DECIMALS)));

        const safePrice = esirP > 0 ? esirP : 1;
        const apyVal = ((((1 + (safePrice / (0.7507 * 1000))) ** 365) - 1) * 100);

        let pendingVal = 0;
        let portfolioBalances = { ebondsWallet: 0, ebondsStaked: 0, esirWallet: 0, esirUnclaimed: 0 };

        if (account) {
             try {
                const [userInfo, walletBalanceRaw, esirBalanceRaw, pendingRaw, vestingInfo] = await Promise.all([
                    contract.userInfo(account),
                    tokenContract.balanceOf(account),
                    esirContract.balanceOf(account),
                    contract.pending({ from: account }),
                    saleContract.getVestingInfo(account),
                ]);

                // Sync redux balances used by the Stake / Unstake cards
                dispatch(setStakeBalance(userInfo.amount.toString()));
                dispatch(setWalletBalance(walletBalanceRaw.toString()));

                const pendingFloat = parseFloat(ethers.utils.formatUnits(pendingRaw, FIXED_DECIMALS));
                const vestingUnclaimed = parseFloat(ethers.utils.formatUnits(vestingInfo[3], FIXED_DECIMALS));
                pendingVal = Math.floor(pendingFloat);

                portfolioBalances = {
                    ebondsWallet:  parseFloat(ethers.utils.formatUnits(walletBalanceRaw, FIXED_DECIMALS)),
                    ebondsStaked:  parseFloat(ethers.utils.formatUnits(userInfo.amount,   FIXED_DECIMALS)),
                    esirWallet:    parseFloat(ethers.utils.formatUnits(esirBalanceRaw,    FIXED_DECIMALS)),
                    esirUnclaimed: vestingUnclaimed + pendingFloat,
                };
             } catch(e) {
                console.warn("User data fetch error", e);
             }
        }

        setBalances(portfolioBalances);
        setStats({
            tvl: totalDeposits,
            totalDistributed: paidOut,
            apy: apyVal,
            esirPrice: esirP,
            ebondPrice: ebondP,
            myPending: pendingVal
        });
        setLoading(false);
    } catch (error) {
        console.error("Staking Data Error:", error);
        setLoading(false);
    }
}, [account, dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- CLAIM FUNCTION ---
    const handleClaim = async () => {
        if (!account || !library) return;
        try {
            setClaiming(true);
            const signer = library.getSigner();
            const contract = new ethers.Contract(stakingContractAddress, abi, signer);

            // Calling withdraw(0) triggers the internal harvest() function in your contract
            const tx = await contract.withdraw(0);
            await tx.wait();

            fetchData(); // Refresh data after transaction
        } catch (error) {
            console.error("Claim Transaction Error:", error);
        } finally {
            setClaiming(false);
        }
    };

    const calculateROI = () => {
        if (!stats.ebondPrice) return "0.00%";
        const roi = (stats.ebondPrice / 0.7507) - 1;
        return `${(roi * 100).toFixed(2)}%`;
    };

    // --- Portfolio overview derived values ---
    const totalEbondsHoldings = balances.ebondsWallet + balances.ebondsStaked;
    const totalEsirHoldings   = balances.esirWallet   + balances.esirUnclaimed;
    const ebondsValue = totalEbondsHoldings * stats.ebondPrice;
    const esirValue   = totalEsirHoldings   * stats.esirPrice;

    const formatWhole = (num) => Math.floor(num || 0).toLocaleString();

    return (
        <Box sx={{ pb: 8, pt: { xs: 12, md: 20 } }}>
            <Container maxWidth="xl">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={700}>
                            PORTFOLIO OVERVIEW
                        </Typography>
                        <Typography variant="h2" gutterBottom>Dashboard</Typography>
                        <Typography variant="h6" color="text.secondary" fontWeight={400}>
                            Real-time market
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {account && (
                            <Chip
                                icon={<AccountBalanceWalletIcon sx={{ fontSize: '1rem !important' }} />}
                                label={`${account.substring(0, 6)}...${account.substring(account.length - 4)}`}
                                sx={{ bgcolor: 'primary.main', color: isDark ? '#000' : '#fff', fontWeight: 700 }}
                            />
                        )}
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            variant="outlined"
                            size="small"
                            disabled={loading}
                            sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>

                {/* ── Yield stats ── */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} md={6}>
                        <CockpitStat
                            compact
                            label="ESIR APY"
                            value={loading ? <Skeleton /> : `${stats.apy.toFixed(2)}%`}
                            highlight={true}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CockpitStat
                            compact
                            label="EBONDS ROI"
                            value={loading ? <Skeleton /> : calculateROI()}
                            highlight={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                            Note: ESIR APY & EBONDS ROI based on average OTC price for Bots Layer Seeding.
                        </Typography>
                    </Grid>
                </Grid>

                {/* ── Portfolio holdings ── */}
                <Grid container spacing={4} sx={{ mb: 6 }}>
                    {/* EBONDS stat */}
                    <Grid item xs={12} md={6}>
                        <StatBox
                            label="TOTAL EBONDS HOLDINGS"
                            value={formatWhole(totalEbondsHoldings)}
                            subValue={`≈ $${ebondsValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon={<AccountBalanceWalletIcon sx={{ color: 'primary.main', opacity: 0.5 }} />}
                            breakdown={
                                <>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">WALLET</Typography>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">{formatWhole(balances.ebondsWallet)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">STAKED</Typography>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">{formatWhole(balances.ebondsStaked)}</Typography>
                                    </Grid>
                                </>
                            }
                        />
                    </Grid>

                    {/* ESIR stat */}
                    <Grid item xs={12} md={6}>
                        <StatBox
                            label="TOTAL ESIR HOLDINGS"
                            value={formatWhole(totalEsirHoldings)}
                            subValue={`≈ $${esirValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            icon={<AccountBalanceWalletIcon sx={{ color: 'primary.main', opacity: 0.5 }} />}
                            breakdown={
                                <>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">WALLET</Typography>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">{formatWhole(balances.esirWallet)}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">UNCLAIMED</Typography>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">{formatWhole(balances.esirUnclaimed)}</Typography>
                                    </Grid>
                                </>
                            }
                        />
                    </Grid>
                </Grid>

                {/* ── Staking widgets ── */}
                <Box sx={{ position: 'relative' }}>
                    {!account && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            zIndex: 10,
                            backdropFilter: 'blur(8px)',
                            bgcolor: 'rgba(10, 16, 25, 0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
                            <Typography variant="h5" fontWeight={700} gutterBottom>Wallet Not Connected</Typography>
                            <Button variant="contained" onClick={() => activate(injected)}>Connect Wallet</Button>
                        </Box>
                    )}

                    <Stack spacing={4}>
                        {/* My Rewards */}
                        <Paper sx={{ p: 4, borderRadius: 2 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" gutterBottom>My Rewards</Typography>
                                    <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                                    <Typography variant="body2" color="text.secondary">CLAIMABLE ESIR</Typography>
                                    <Typography variant="h3" color="primary.main" fontWeight={700}>
                                        {loading ? <Skeleton width={120} /> : stats.myPending.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ≈ ${(stats.myPending * stats.esirPrice).toFixed(2)} USD
                                    </Typography>
                                </Box>

                                <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
                                    {/* --- CLAIM BUTTON --- */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<GetAppIcon />}
                                        onClick={handleClaim}
                                        disabled={claiming || stats.myPending <= 0}
                                        sx={{ py: 1.5, fontWeight: 700 }}
                                    >
                                        {claiming ? 'Processing...' : 'Claim ESIR Rewards'}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                        Note: Claiming rewards will harvest your ESIR tokens to your wallet while keeping your EBONDS staked.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <StakeCard update={fetchData} />
                        <WithdrawCard update={fetchData} />

                        {/* Recent History */}
                        <Paper sx={{ p: 4, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <HistoryIcon sx={{ color: 'text.secondary' }} />
                                <Typography variant="h6">Recent History</Typography>
                            </Stack>
                            <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">No recent activity found.</Typography>
                            </Box>
                        </Paper>
                    </Stack>
                </Box>

                {/* ── Protocol totals ── */}
                <Grid container spacing={3} sx={{ mt: 6 }}>
                    <Grid item xs={12} md={6}>
                        <CockpitStat
                            label="TOTAL EBONDS STAKING"
                            value={loading ? <Skeleton /> : `${stats.tvl.toLocaleString()} EBONDS`}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CockpitStat
                            label="TOTAL ESIR DISTRIBUTED"
                            value={loading ? <Skeleton /> : `${stats.totalDistributed.toLocaleString()} ESIR`}
                        />
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default AllocationStaking;
